import PratusInput from '../../../DesignSystems/New/PratusInput';
import PratusSelect from '../../../DesignSystems/New/PratusSelect';
import PratusTextArea from '../../../DesignSystems/New/PratusTextArea';
import { StylishNewCheckbox } from '../../../DesignSystems/New/StylishNewCheckbox';
import { StylishSwitcher } from '../../../DesignSystems/New/StylishSwitcher';
import AIReviewButton from 'components/DisasterGPT/AIReviewButton';
import React, { useRef, useState } from 'react';
import MaterialsTable, { MaterialRow } from './MaterialsTable';
import {
  GetHazmatResponse,
  IncidentInfoResponse,
  Material,
  useGetSafetyAnalysis,
  useUpdateSafetyAnalysis,
} from '../../hooks/useSafetyAnalysis';
import { SafetyMessageDto } from '../../../../types/iapBaseTypes';
import {
  HAZMATClassification,
  HazmatOption,
  IncidentNeed,
  IncidentNeedOption,
} from './types';
import { useSafetyMessages } from '../../hooks/useSafetyMessages';
import {
  ENVIRONMENTAL_HAZARD_OPTIONS,
  HAZMAT_CLASSIFICATION_OPTIONS,
  INCIDENT_NEED_OPTIONS,
  OTHER_HAZARD_OPTIONS,
  PHYSICAL_HAZARD_OPTIONS,
  POTENTIAL_HAZARD_OPTIONS,
  REQUIRED_PPE_OPTIONS,
  withStoredValues,
} from 'components/IAP/ICSConstants';

// The hazard/PPE fields are plain string[]: the backing columns are unconstrained text[], and the DGPT agent may
// persist values outside the controlled vocabularies in ./types.ts — those render as custom chips below.
export type SafetyAnalysisFormState = {
  safetyOfficer: string | null;
  physicalHazards: string[] | null;
  environmentalHazards: string[] | null;
  otherHazards: string[] | null;
  weatherConditions: {
    temp?: string | null;
    conditions?: string | null;
    wind?: string | null;
    tides?: string | null;
    seaState?: string | null;
    waterTemp?: string | null;
    forecast?: string | null;
  };
  safetyNotes: string | null;
  requiredPPE: string[] | null;
  notesOnPPE: string | null;
  hazmatClassification: HAZMATClassification[] | null;
  otherClassification: string | null;
  material: Material[] | null;
  potentialHazards: string[] | null;
  incidentNeeds: IncidentNeed[] | null;
  airMonitoringRequired: boolean;
  standardOperatingProcedures: string | null;
  decontaminationProcedures: string | null;
  medicalMonitoringRequired: boolean;
  medicalTreatmentAndTransportInPlace: boolean;
  emergencyProcedures: string | null;
};

type BuildInitialData = (
  hazmatData: GetHazmatResponse | undefined,
  icsInfoData: IncidentInfoResponse | undefined,
  safetyMessagesData: SafetyMessageDto[] | undefined
) => SafetyAnalysisFormState;
const buildInitialData: BuildInitialData = (
  hazmatData,
  icsInfoData,
  safetyMessagesData
) => ({
  safetyOfficer: safetyMessagesData?.[0]?.safetyOfficer ?? null,
  physicalHazards: icsInfoData?.physicalHazards ?? null,
  environmentalHazards: icsInfoData?.environmentalHazards ?? null,
  otherHazards: icsInfoData?.otherHazards ?? null,
  weatherConditions: icsInfoData?.weather ?? {
    temp: null,
    conditions: null,
    wind: null,
    tides: null,
    seaState: null,
    waterTemp: null,
    forecast: null,
  },
  safetyNotes: safetyMessagesData?.[0]?.comments ?? null,
  requiredPPE: icsInfoData?.requiredPpe ?? null,
  notesOnPPE: icsInfoData?.notesOnPpe ?? null,
  potentialHazards: icsInfoData?.potentialHazards ?? null,

  hazmatClassification: hazmatData?.classifications ?? null,
  otherClassification: hazmatData?.otherClassification ?? null,
  material: hazmatData?.material ?? null,
  incidentNeeds: hazmatData?.incidentNeeds ?? null,
  airMonitoringRequired: hazmatData?.airMonitoring ?? false,
  standardOperatingProcedures: hazmatData?.sops ?? null,
  decontaminationProcedures: hazmatData?.decontaminationProcedure ?? null,
  medicalMonitoringRequired: hazmatData?.medicalMonitoring ?? false,
  medicalTreatmentAndTransportInPlace: hazmatData?.medicalTreatment ?? false,
  emergencyProcedures: hazmatData?.emergencyProcedures ?? null,
  additionalProductInfo: null, // not yet implemented on this form
  hazardMonitoringComments: null, // not yet implemented on this form
  lelInstruments: null, // not yet implemented on this form
  o2Instruments: null, // not yet implemented on this form
  radiologicalInstruments: null, // not yet implemented on this form
  toxicityInstruments: null, // not yet implemented on this form
});

const SafetyAnalysis = () => {
  const [hazmatQuery, icsInfoQuery, safetyMessagesQuery] =
    useGetSafetyAnalysis();

  if (
    hazmatQuery.isLoading ||
    icsInfoQuery.isLoading ||
    safetyMessagesQuery.isLoading
  ) {
    return <div className="d-flex justify-content-center">Loading...</div>;
  }
  const initialState = buildInitialData(
    hazmatQuery.data,
    icsInfoQuery.data,
    safetyMessagesQuery.data
  );

  return (
    // Keyed on the backing record versions: the form seeds its local state
    // from initialState ONCE (useState below), so an external write — e.g.
    // the AI agent updating the HAZMAT record or incident info — must remount
    // it to show fresh data. ICS rows are insert-versioned, so any write
    // yields a new id/version.
    <SafetyAnalysisForm
      key={[
        hazmatQuery.data?.id ?? 'no-hazmat',
        hazmatQuery.data?.version ?? 0,
        icsInfoQuery.data?.id ?? 'no-info',
        icsInfoQuery.data?.version ?? 0,
        safetyMessagesQuery.data?.[0]?.id ?? 'no-msg',
      ].join(':')}
      initialState={initialState}
      initialShowHazmat={Boolean(hazmatQuery.data)}
    />
  );
};

interface SafetyAnalysisFormProps {
  initialState: SafetyAnalysisFormState;
  initialShowHazmat: boolean;
}
const SafetyAnalysisForm = ({
  initialState,
  initialShowHazmat,
}: SafetyAnalysisFormProps) => {
  const [formState, setFormState] = useState(initialState);
  const {
    updateIncidentInfo,
    createSafetyMessage,
    updateSafetyMessage,
    postHazmats,
  } = useUpdateSafetyAnalysis(formState);
  const safetyMessagesQuery = useSafetyMessages();
  const [showHazmat, setShowHazmat] = useState(initialShowHazmat);
  const safetyMessageId = safetyMessagesQuery.data?.[0]?.id;

  // Persist-on-commit helpers: update local state and immediately save the
  // relevant slice to its backend endpoint. The fresh `next` state is passed to
  // the mutation to avoid sending stale form state (setFormState is async).
  const persistIncidentInfo = (next: SafetyAnalysisFormState) => {
    setFormState(next);
    pendingSelects.current.incidentInfo = false;
    updateIncidentInfo.mutate(next);
  };
  const persistHazmats = (next: SafetyAnalysisFormState) => {
    setFormState(next);
    pendingSelects.current.hazmats = false;
    postHazmats.mutate(next);
  };

  // The multiselects stage locally and persist ONCE when their menu closes: every ICS write inserts a new record
  // version, the refetch remounts this form (the version-based `key` above), and a remount mid-interaction tears
  // down the open menu — so persisting per checkbox click made multi-selection one-click-at-a-time.
  const pendingSelects = useRef({ incidentInfo: false, hazmats: false });
  const stagedFormState = useRef(formState);
  const stageIncidentInfo = (next: SafetyAnalysisFormState) => {
    setFormState(next);
    stagedFormState.current = next;
    pendingSelects.current.incidentInfo = true;
  };
  const stageHazmats = (next: SafetyAnalysisFormState) => {
    setFormState(next);
    stagedFormState.current = next;
    pendingSelects.current.hazmats = true;
  };
  const persistStagedSelects = () => {
    if (pendingSelects.current.incidentInfo) {
      pendingSelects.current.incidentInfo = false;
      updateIncidentInfo.mutate(stagedFormState.current);
    }
    if (pendingSelects.current.hazmats) {
      pendingSelects.current.hazmats = false;
      postHazmats.mutate(stagedFormState.current);
    }
  };
  const persistSafetyMessage = (next: SafetyAnalysisFormState) => {
    setFormState(next);
    if (safetyMessageId) {
      updateSafetyMessage.mutate({ safetyMessageId, form: next });
    } else if (!createSafetyMessage.isPending) {
      // Guard against creating duplicate safety messages while a create is in
      // flight; the POST endpoint always inserts a new row.
      createSafetyMessage.mutate(next);
    }
  };

  // Canonical options plus custom chips for stored out-of-vocab values (e.g. agent-written hazards) — without the
  // merge, such values are silently hidden and the next manual edit of that dropdown erases them.
  const physicalHazardOptions = withStoredValues(
    PHYSICAL_HAZARD_OPTIONS,
    formState.physicalHazards
  );
  const environmentalHazardOptions = withStoredValues(
    ENVIRONMENTAL_HAZARD_OPTIONS,
    formState.environmentalHazards
  );
  const otherHazardOptions = withStoredValues(
    OTHER_HAZARD_OPTIONS,
    formState.otherHazards
  );
  const requiredPpeOptions = withStoredValues(
    REQUIRED_PPE_OPTIONS,
    formState.requiredPPE
  );
  const potentialHazardOptions = withStoredValues(
    POTENTIAL_HAZARD_OPTIONS,
    formState.potentialHazards
  );

  return (
    <div className="safety-analysis-container d-flex flex-column gap-3">
      <div className="d-flex justify-content-end">
        <AIReviewButton actionKey="safetyAnalysis" />
      </div>
      <PratusInput
        label="Safety Officer"
        placeholder="Enter a safety officer..."
        value={formState.safetyOfficer ?? undefined}
        onSave={(value) =>
          persistSafetyMessage({ ...formState, safetyOfficer: value })
        }
      />
      <PratusSelect
        label="Physical Hazards"
        placeholder="Click to select physical hazards..."
        options={physicalHazardOptions}
        value={physicalHazardOptions.filter((option) =>
          formState.physicalHazards?.includes(option.value)
        )}
        onChange={(value) => {
          const newValues =
            (value as { value: string }[] | null)?.map((v) => v.value) ?? [];
          stageIncidentInfo({ ...formState, physicalHazards: newValues });
        }}
        isMulti
        onMenuClose={persistStagedSelects}
      />
      <PratusSelect
        label="Environmental Hazards"
        placeholder="Click to select environmental hazards..."
        options={environmentalHazardOptions}
        value={environmentalHazardOptions.filter((option) =>
          formState.environmentalHazards?.includes(option.value)
        )}
        onChange={(value) => {
          const newValues =
            (value as { value: string }[] | null)?.map((v) => v.value) ?? [];
          stageIncidentInfo({
            ...formState,
            environmentalHazards: newValues,
          });
        }}
        isMulti
        onMenuClose={persistStagedSelects}
      />
      <PratusSelect
        label="Other Hazards"
        placeholder="Click to select other hazards..."
        options={otherHazardOptions}
        value={otherHazardOptions.filter((option) =>
          formState.otherHazards?.includes(option.value)
        )}
        onChange={(value) => {
          const newValues =
            (value as { value: string }[] | null)?.map((v) => v.value) ?? [];
          stageIncidentInfo({ ...formState, otherHazards: newValues });
        }}
        isMulti
        onMenuClose={persistStagedSelects}
      />
      <div className="d-flex flex-column weather-conditions gap-2">
        <>Weather Conditions:</>
        <div className="d-flex flex-row flex-wrap gap-1">
          <PratusInput
            label="Temp:"
            placeholder="Enter temp..."
            value={formState.weatherConditions.temp ?? undefined}
            onSave={(value) =>
              persistIncidentInfo({
                ...formState,
                weatherConditions: {
                  ...formState.weatherConditions,
                  temp: value,
                },
              })
            }
          />
          <PratusInput
            label="Conditions:"
            placeholder="Enter conditions..."
            value={formState.weatherConditions.conditions ?? undefined}
            onSave={(value) =>
              persistIncidentInfo({
                ...formState,
                weatherConditions: {
                  ...formState.weatherConditions,
                  conditions: value,
                },
              })
            }
          />
          <PratusInput
            label="Wind:"
            placeholder="Enter wind..."
            value={formState.weatherConditions.wind ?? undefined}
            onSave={(value) =>
              persistIncidentInfo({
                ...formState,
                weatherConditions: {
                  ...formState.weatherConditions,
                  wind: value,
                },
              })
            }
          />
          <PratusInput
            label="Tides:"
            placeholder="Enter tides..."
            value={formState.weatherConditions.tides ?? undefined}
            onSave={(value) =>
              persistIncidentInfo({
                ...formState,
                weatherConditions: {
                  ...formState.weatherConditions,
                  tides: value,
                },
              })
            }
          />
          <PratusInput
            label="Sea State:"
            placeholder="Enter sea..."
            value={formState.weatherConditions.seaState ?? undefined}
            onSave={(value) =>
              persistIncidentInfo({
                ...formState,
                weatherConditions: {
                  ...formState.weatherConditions,
                  seaState: value,
                },
              })
            }
          />
          <PratusInput
            label="Water Temp:"
            placeholder="Enter water..."
            value={formState.weatherConditions.waterTemp ?? undefined}
            onSave={(value) =>
              persistIncidentInfo({
                ...formState,
                weatherConditions: {
                  ...formState.weatherConditions,
                  waterTemp: value,
                },
              })
            }
          />
          <PratusInput
            label="Forecast:"
            placeholder="Enter forecast..."
            value={formState.weatherConditions.forecast ?? undefined}
            onSave={(value) =>
              persistIncidentInfo({
                ...formState,
                weatherConditions: {
                  ...formState.weatherConditions,
                  forecast: value,
                },
              })
            }
          />
        </div>
      </div>
      <PratusTextArea
        label="Safety Notes"
        placeholder="Enter a description..."
        value={formState.safetyNotes ?? undefined}
        onSave={(value) =>
          persistSafetyMessage({ ...formState, safetyNotes: value })
        }
        rows={10}
      />
      <PratusSelect
        label="Required Personal Protective Equipment (PPE)"
        placeholder="Click to select PPE..."
        options={requiredPpeOptions}
        value={requiredPpeOptions.filter((option) =>
          formState.requiredPPE?.includes(option.value)
        )}
        onChange={(value) => {
          const newValues =
            (value as { value: string }[] | null)?.map((v) => v.value) ?? [];
          stageIncidentInfo({ ...formState, requiredPPE: newValues });
        }}
        isMulti
        onMenuClose={persistStagedSelects}
      />
      <PratusTextArea
        label="Notes on PPE"
        placeholder="Enter a description..."
        value={formState.notesOnPPE ?? undefined}
        onSave={(value) =>
          persistIncidentInfo({ ...formState, notesOnPPE: value })
        }
      />
      <div className="d-flex align-items-center hazmat-row flex-row gap-2">
        <StylishSwitcher
          className="hazmat-switch"
          value="Does the incident involve Hazardous Materials (HAZMAT)?"
          checked={showHazmat}
          onChange={() => setShowHazmat(!showHazmat)}
        />
        <>Does the incident involve Hazardous Materials (HAZMAT)?</>
      </div>

      <div
        className={`hazmat-container flex-column gap-2 ${showHazmat ? 'd-flex' : 'd-none'}`}
      >
        <PratusSelect
          label="HAZMAT Classification"
          placeholder="Click to select hazardous materials..."
          options={HAZMAT_CLASSIFICATION_OPTIONS}
          value={HAZMAT_CLASSIFICATION_OPTIONS.filter((option) =>
            formState.hazmatClassification?.includes(option.value)
          )}
          onChange={(value) => {
            const newValues =
              (value as HazmatOption[] | null)?.map((v) => v.value) ?? [];
            stageHazmats({ ...formState, hazmatClassification: newValues });
          }}
          isMulti
          onMenuClose={persistStagedSelects}
        />
        {formState.hazmatClassification?.includes('Other/Miscellaneous') && (
          <PratusTextArea
            label="Other/Miscellaneous HAZMAT Classifications"
            placeholder="Enter a classification..."
            value={formState.otherClassification ?? undefined}
            onSave={(value) =>
              persistHazmats({ ...formState, otherClassification: value })
            }
            rows={2}
          />
        )}
        <div className="mt-2">
          <MaterialsTable
            value={formState.material}
            onChange={(rows: MaterialRow[]) => {
              persistHazmats({ ...formState, material: rows });
            }}
          />
        </div>
        <PratusSelect
          label="Potential Hazards"
          placeholder="Click to select potential hazards..."
          options={potentialHazardOptions}
          value={potentialHazardOptions.filter((option) =>
            formState.potentialHazards?.includes(option.value)
          )}
          onChange={(value) => {
            const newValues =
              (value as { value: string }[] | null)?.map((v) => v.value) ?? [];
            stageIncidentInfo({ ...formState, potentialHazards: newValues });
          }}
          isMulti
          onMenuClose={persistStagedSelects}
        />
        <PratusSelect
          label="Does the incident require a need for any of the following?"
          placeholder="Click to select required information..."
          options={INCIDENT_NEED_OPTIONS}
          value={INCIDENT_NEED_OPTIONS.filter((option) =>
            formState.incidentNeeds?.includes(option.value)
          )}
          onChange={(value) => {
            const newValues =
              (value as IncidentNeedOption[] | null)?.map((v) => v.value) ?? [];
            stageHazmats({ ...formState, incidentNeeds: newValues });
          }}
          isMulti
          onMenuClose={persistStagedSelects}
        />
        <StylishNewCheckbox
          label="Air Monitoring Required?"
          checked={formState.airMonitoringRequired}
          onClick={() =>
            persistHazmats({
              ...formState,
              airMonitoringRequired: !formState.airMonitoringRequired,
            })
          }
        />
        <PratusTextArea
          label="Standard Operating Procedures (SOP) and Safe Work Practices"
          placeholder="Enter a description..."
          value={formState.standardOperatingProcedures ?? undefined}
          onSave={(value) =>
            persistHazmats({
              ...formState,
              standardOperatingProcedures: value,
            })
          }
          rows={10}
        />
        <PratusTextArea
          label="Decontamination Procedures:"
          placeholder="Enter a description..."
          value={formState.decontaminationProcedures ?? undefined}
          onSave={(value) =>
            persistHazmats({
              ...formState,
              decontaminationProcedures: value,
            })
          }
          rows={10}
        />
        <div className="d-flex align-items-left medical-row flex-row gap-4">
          <>Medical:</>
          <StylishNewCheckbox
            label="Medical Monitoring Required?"
            checked={formState.medicalMonitoringRequired}
            onClick={() =>
              persistHazmats({
                ...formState,
                medicalMonitoringRequired: !formState.medicalMonitoringRequired,
              })
            }
          />
          <StylishNewCheckbox
            label="Medical Treatment and Transport in place?"
            checked={formState.medicalTreatmentAndTransportInPlace}
            onClick={() =>
              persistHazmats({
                ...formState,
                medicalTreatmentAndTransportInPlace:
                  !formState.medicalTreatmentAndTransportInPlace,
              })
            }
          />
        </div>
        <PratusTextArea
          label="Emergency Procedures:"
          placeholder="Enter a description..."
          value={formState.emergencyProcedures ?? undefined}
          onSave={(value) =>
            persistHazmats({ ...formState, emergencyProcedures: value })
          }
          rows={10}
        />
      </div>
    </div>
  );
};

export default SafetyAnalysis;
